import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { cn } from "@core/utils";
import { Icon } from "@core/assets";

type ButtonVariant = "primary" | "secondary" | "soft" | "tertiary" | "link";

interface ButtonProps extends Omit<AriaButtonProps, "className"> {
  variant?: ButtonVariant;
  /** Stretch to fill the container's inline size. */
  fullWidth?: boolean;
  className?: string;
}

/**
 * The app's button primitive (own-the-code) built on React Aria's
 * `Button`. Colors/spacing come from the semantic design tokens (theme.css), never raw hex.
 * State styling uses the tailwindcss-react-aria-components variants (`pressed:`, `disabled:`).
 *
 * `isPending` renders a spinner (from the shared icon registry) and marks the control busy —
 * so every async action gets a consistent in-flight affordance without callers hand-rolling
 * one. Pair it with `isDisabled` to also block interaction while pending (see forms.md).
 */
const BASE =
  "inline-flex items-center justify-center gap-xs rounded-md outline-none transition-colors " +
  "focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 " +
  "disabled:cursor-not-allowed pending:cursor-progress";

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "px-md py-2.5 text-sm-semibold text-(--action-primary-fg) shadow-xs " +
    "bg-action-primary hover:bg-action-primary-hover pressed:bg-action-primary-hover " +
    "disabled:bg-action-primary-disabled",
  secondary:
    "px-md py-2.5 text-sm-semibold text-(--action-secondary-fg) shadow-xs " +
    "border border-(--action-secondary-border) bg-surface-card " +
    "hover:bg-gray-50 pressed:bg-gray-100 disabled:text-(--text-disabled)",
  // Brand-tinted, borderless — the design's card-level primary (Figma 01 · Appels, 408:21839),
  // where a solid brand fill on every card in a grid would fight for attention with the page's
  // one real primary ("Créer un appel"). Built from the existing brand scale paired with
  // `--text-link`, the same combination `RoundsEmptyState` already uses, so it introduces no
  // colour — only a rank the token layer could already express.
  soft:
    "px-md py-2.5 text-sm-semibold text-(--text-link) shadow-xs " +
    "bg-brand-50 hover:bg-brand-100 pressed:bg-brand-100 disabled:text-(--text-disabled)",
  // Same footprint as `secondary` but with no border or fill — the design's third-rank action
  // ("View intake report" on a closed round). `--action-tertiary-fg` already existed in the
  // token layer for exactly this, so no new colour was needed.
  tertiary:
    "px-md py-2.5 text-sm-semibold text-(--action-tertiary-fg) " +
    "hover:bg-gray-50 pressed:bg-gray-100 disabled:text-(--text-disabled)",
  link:
    "rounded-xs text-sm-semibold text-(--text-link) " +
    "hover:underline underline-offset-2 disabled:text-(--text-disabled)",
};

export function Button({
  variant = "primary",
  fullWidth = false,
  className,
  children,
  isPending,
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      isPending={isPending}
      className={cn(BASE, VARIANTS[variant], fullWidth && "w-full", className)}
      {...props}
    >
      {(renderProps) => (
        <>
          {isPending ? (
            <Icon name="loading" size={18} className="animate-spin" />
          ) : null}
          {typeof children === "function" ? children(renderProps) : children}
        </>
      )}
    </AriaButton>
  );
}

export type { ButtonProps, ButtonVariant };
